import { useSyncExternalStore } from "react";

// Saved searches ("search folders"): a name and a query, listed in the folder pane. Kept per browser and per signed-in user.

export interface SavedSearch { id: string; name: string; query: string }

const MAX = 25;
const listeners = new Set<() => void>();
let currentUser = "";
let cache: SavedSearch[] | null = null;

const key = () => `fg:mail:saved-searches:${currentUser || "anon"}`;

function read(): SavedSearch[] {
    if (cache) return cache;
    try {
        const raw = JSON.parse(localStorage.getItem(key()) ?? "[]") as unknown;
        cache = Array.isArray(raw)
            ? raw.filter((s): s is SavedSearch => typeof s?.id === "string" && typeof s?.name === "string" && typeof s?.query === "string").slice(0, MAX)
            : [];
    } catch {
        cache = [];
    }
    return cache;
}

function write(next: SavedSearch[]) {
    cache = next;
    try { localStorage.setItem(key(), JSON.stringify(next)); } catch { /* kept for this session only */ }
    listeners.forEach((l) => l());
}

/** Tell the store whose list to use (the signed-in email). */
export function setSavedSearchUser(email: string | undefined) {
    const next = (email ?? "").toLowerCase();
    if (next === currentUser) return;
    currentUser = next;
    cache = null;
    listeners.forEach((l) => l());
}

export function addSavedSearch(name: string, query: string) {
    const q = query.trim();
    const n = name.trim() || q;
    if (!q) return;
    const list = read().filter((s) => s.query.toLowerCase() !== q.toLowerCase());
    write([{ id: `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`, name: n.slice(0, 60), query: q }, ...list].slice(0, MAX));
}

export function removeSavedSearch(id: string) {
    write(read().filter((s) => s.id !== id));
}

export function useSavedSearches(): SavedSearch[] {
    return useSyncExternalStore(
        (cb) => {
            listeners.add(cb);
            const onStorage = (e: StorageEvent) => { if (e.key === key()) { cache = null; cb(); } };
            window.addEventListener("storage", onStorage);
            return () => { listeners.delete(cb); window.removeEventListener("storage", onStorage); };
        },
        read
    );
}
